import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

import { authService } from '../../services/api';
import AuthShell from './AuthShell';

const ForgotPassword = () => {
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      await authService.requestPasswordReset(identifier.trim());
      setSubmitted(true);
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'We could not process the request. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Account recovery"
      title={submitted ? 'Check your inbox' : 'Reset your password'}
      description={
        submitted
          ? 'If an active Timelyzer account matches those details, a secure reset link is on its way.'
          : 'Enter the email address or username connected to your account. We will send a single-use reset link.'
      }
    >
      {submitted ? (
        <div>
          <div className="border-y border-[#dce3dd] py-6">
            <div className="flex items-start gap-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#deede5] text-[#246b52]">
                <CheckCircleIcon className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-bold text-[#26342c]">Reset request received</p>
                <p className="mt-2 text-sm leading-6 text-[#65716a]">
                  The link expires after 30 minutes. Check your spam folder if it does not arrive shortly.
                </p>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              setSubmitted(false);
              setIdentifier('');
            }}
            className="secondary-button mt-6 w-full"
          >
            Try another account
          </button>
          <Link to="/login" className="mt-3 inline-flex w-full items-center justify-center gap-2 py-2 text-sm font-bold text-[#246b52] hover:text-[#18513d]">
            <ArrowLeftIcon className="h-4 w-4" />
            Back to sign in
          </Link>
        </div>
      ) : (
        <>
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="identifier" className="field-label">Email or username</label>
              <div className="relative">
                <EnvelopeIcon className="pointer-events-none absolute left-3.5 top-3.5 h-4 w-4 text-[#7c8780]" />
                <input
                  id="identifier"
                  name="identifier"
                  type="text"
                  autoComplete="username"
                  required
                  value={identifier}
                  onChange={(event) => setIdentifier(event.target.value)}
                  className="field-input pl-10"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            {error && (
              <p role="alert" className="rounded-md border border-[#e5c7be] bg-[#fbf2ef] px-3 py-2 text-sm text-[#934632]">
                {error}
              </p>
            )}

            <button type="submit" disabled={loading || !identifier.trim()} className="primary-button w-full disabled:cursor-not-allowed disabled:opacity-60">
              {loading ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Sending reset link
                </>
              ) : (
                <>
                  Send reset link
                  <ArrowRightIcon className="h-4 w-4" />
                </>
              )}
            </button>
          </form>

          <div className="mt-7 border-t border-[#dce3dd] pt-6 text-center">
            <Link to="/login" className="inline-flex items-center gap-2 text-sm font-bold text-[#246b52] hover:text-[#18513d]">
              <ArrowLeftIcon className="h-4 w-4" />
              Back to sign in
            </Link>
          </div>
        </>
      )}
    </AuthShell>
  );
};

const getErrorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg.replace(/^Value error, /, '');
  return fallback;
};

export default ForgotPassword;
